import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Spinner, Modal } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { mockRoles } from '../data/mockData';
import { getSmartUser, updateUser, fetchEntityList } from '../services/api';
import { getCurrentUser } from '../services/authService';
import { USER_ROLES } from '../lib/utils/constant';

function EditUser() {
  const { id } = useParams();
  const navigate = useNavigate();

  // --- FIX: Stabilize currentUser to prevent infinite useEffect loops ---
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const user = getCurrentUser();
    if (user) {
      setCurrentUser(user);
    } else {
      navigate('/login');
    }
  }, [navigate]);
  // ---------------------------------------------------------------------

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false); // New state for saving
  const [userType, setUserType] = useState('client');
  const [formData, setFormData] = useState({
    entity: '',
    client: '',
    role: '',
    firstName: '',
    lastName: '',
    email: ''
  });

  const [entitiesList, setEntitiesList] = useState([]); // Raw Entity List from API
  const [availableClients, setAvailableClients] = useState([]);

  // --- Dynamic Visibility State ---
  const [visibleUserTypes, setVisibleUserTypes] = useState([]);
  const [isEntityLocked, setIsEntityLocked] = useState(false);
  const [isClientLocked, setIsClientLocked] = useState(false);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [roleDisplayName, setRoleDisplayName] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const isClientAccount = currentUser?.entityMaster?.accountType === USER_ROLES.CLIENT;
  const isEntityAccount = currentUser?.entityMaster?.accountType === USER_ROLES.ENTITY;

   // --- 1. Load Entities & Clients ---
  useEffect(() => {
    const loadEntities = async () => {
      try {
        const response = await fetchEntityList();
        if (response.data && response.data.success && response.data.data?.items) {
          const fetchedItems = response.data.data.items;
          setEntitiesList((prev) => {
            const prevItems = Array.isArray(prev) ? prev : [];
            const mergedMap = new Map();
            prevItems.forEach((item) => mergedMap.set(String(item.id), item));
            fetchedItems.forEach((item) => mergedMap.set(String(item.id), item));
            return Array.from(mergedMap.values());
          });
        }
      } catch (error) {
        console.error("Error loading entities:", error);
      }
    };
    loadEntities();
  }, []);

const populateForm = (u, currentEntitiesList) => {
    const normalizeId = (value) => (value === null || value === undefined ? '' : String(value));
    let type = 'client'; // default fallback
    let entId = '';
    let clId = '';
    const roleCode = (u.rolecode || u.role?.code || '').toUpperCase();
    setRoleDisplayName(u.role?.name || u.roleName || u.rolecode || '');

    if (roleCode === USER_ROLES.SYS_USER) {
      type = 'platform';
    }

    // Prefer explicit entity/client objects from API response.
    if (type !== 'platform' && u.entity && u.client) {
      type = 'client';
      entId = normalizeId(u.entity.id);
      clId = normalizeId(u.client.id);
    } else if (type !== 'platform' && u.entity && !u.client) {
      type = 'entity';
      entId = normalizeId(u.entity.id);
      clId = '';
    }
    // Logic for Flat Structure (fallback)
    else if (type !== 'platform' && u.entityMasterId) {
      const targetId = u.entityMasterId;

      // 1. Is it an Entity?
      // Check if targetId matches an entity's ID directly
      const entityMatch = currentEntitiesList.find(e => String(e.id) === String(targetId));

      if (entityMatch) {
        type = 'entity';
        entId = targetId;
      } else {
        // 2. Is it a Client?
        // Iterate through all entities to find if targetId exists in their clients list
        let parentEntityId = null;
        
        for (const ent of currentEntitiesList) {
            if (ent.clients && ent.clients.find(c => String(c.id) === String(targetId))) {
                parentEntityId = ent.id;
                break;
            }
        }

        if (parentEntityId) {
          type = 'client';
          entId = parentEntityId;
          clId = targetId;
        } else {
          // Fallback: If we can't find it in our lists
          console.warn(`EntityMasterId ${targetId} not found in loaded entity list.`);
        }
      }
    }
    // Legacy/Nested Logic (just in case structure varies)
    else if (type !== 'platform' && u.entityMaster) {
      if (u.entityMaster.account_type === 'ENTITY') {
        type = 'entity';
        entId = u.entityMaster.id;
      } else if (u.entityMaster.account_type === 'CLIENT') {
        type = 'client';
        clId = u.entityMaster.id;
        entId = u.entityMaster.parent_id;
      }
    }

    setUserType(type);
    if (type === 'client' && entId) {
      const selectedEntity = currentEntitiesList.find(e => String(e.id) === String(entId));
      if (selectedEntity?.clients?.length) {
        setAvailableClients(selectedEntity.clients);
      } else if (u.client?.id || clId) {
        const selectedClientId = u.client?.id || clId;
        setAvailableClients([{ id: selectedClientId, name: u.client?.name || `Client ${selectedClientId}` }]);
      } else {
        setAvailableClients([]);
      }
    } else {
      setAvailableClients([]);
    }
    setFormData({
      firstName: u.firstName || '',
      lastName: u.lastName || '',
      email: u.emailAddress || u.email || '',
      role: u.roleId || u.role?.id || '',
      entity: normalizeId(entId),
      client: normalizeId(clId)
    });
  };

  // --- 1. Load Data (Entities then User) ---
  useEffect(() => {
    const loadAllData = async () => {
      setIsLoading(true);
      try {

        // 2. Fetch User
        const userRes = await getSmartUser(id);
        if (userRes.data && userRes.data.success) {
          const userData = userRes.data.data;
          let mergedEntities =
            userData?.entity?.id 
              ? [ { ...userData.entity, clients: userData.entity.clients || [] }]
              : [];
          if (userData?.entity?.id && userData?.client?.id) {
            mergedEntities = mergedEntities.map((entity) => {
              if (String(entity.id) !== String(userData.entity.id)) return entity;
              const entityClients = Array.isArray(entity.clients) ? entity.clients : [];
              const hasClient = entityClients.some(
                (client) => String(client.id) === String(userData.client.id)
              );
              if (hasClient) return entity;
              return {
                ...entity,
                clients: [...entityClients, userData.client],
              };
            });
          }
          setEntitiesList((prev) => {
            const prevItems = Array.isArray(prev) ? prev : [];
            const mergedMap = new Map();
            prevItems.forEach((item) => mergedMap.set(String(item.id), item));
            mergedEntities.forEach((item) => mergedMap.set(String(item.id), item));
            return Array.from(mergedMap.values());
          });
          // Use the locally fetched entities list to populate form immediately
          populateForm(userData, mergedEntities);
        } else {
          throw new Error("Failed to fetch admin user data");
        }
      } catch (error) {
        console.error("Error loading data:", error);
        // navigate('/users'); 
      } finally {
        setIsLoading(false);
      }
    };
    loadAllData();
  }, [id, navigate]);
  // --- 2. Permissions Logic (Role Based) ---
  useEffect(() => {
    if (!currentUser) return;
    const roleCode = currentUser.role?.code || currentUser.role?.name || 'USER';
    const accountType = currentUser.entityMaster?.account_type;

    // CLIENT ADMIN
    if (roleCode === 'CLIENT_ADMIN' || (roleCode === 'ADMIN' && accountType === 'CLIENT')) {
      setVisibleUserTypes([]);
      setIsEntityLocked(true);
      setIsClientLocked(true);
      // Filter: Client Admin can only assign 'User' role
      const allowed = mockRoles.filter(r =>
        r.name.toUpperCase() === 'USER' ||
        r.code === 'USER'
      );
      setFilteredRoles(allowed);
    }
    // ENTITY ADMIN
    else if (roleCode === 'ENTITY_ADMIN' || (roleCode === 'ADMIN' && accountType === 'ENTITY')) {
      setVisibleUserTypes(['client']);
      setIsEntityLocked(true);
      setIsClientLocked(false);
      // Filter: Entity Admin cannot assign System/Super Admin roles
      const allowed = mockRoles.filter(r =>
        !r.name.toUpperCase().includes('SYSTEM') &&
        !r.name.toUpperCase().includes('SUPER') &&
        r.code !== 'SYS_ADMIN'
      );
      setFilteredRoles(allowed);
    }
    // SYSTEM ADMIN / SUPER ADMIN
    else {
      setVisibleUserTypes(['platform', 'entity', 'client']);
      setIsEntityLocked(false);
      setIsClientLocked(false);
      setFilteredRoles(mockRoles);

    }
  }, [currentUser]);

  // --- 4. Dependent Dropdowns (Entities -> Clients) ---
  useEffect(() => {
    if (formData.entity && userType === 'client') {
      const selectedEntity = entitiesList.find(
        (e) => String(e.id) === String(formData.entity)
      );

      if (selectedEntity?.clients?.length) {
        setAvailableClients(selectedEntity.clients);
      } else if (formData.client) {
        setAvailableClients((prev) => {
          const alreadyPresent = prev.some(
            (client) => String(client.id) === String(formData.client)
          );
          if (alreadyPresent) return prev;
          return [...prev, { id: formData.client, name: `Client ${formData.client}` }];
        });
      } else {
        setAvailableClients([]);
      }
    } else {
      setAvailableClients([]);
    }
  }, [formData.entity, formData.client, userType, entitiesList]);

  useEffect(() => {
    if(currentUser?.entityMaster?.accountType === USER_ROLES.CLIENT){
      setFormData(prev => ({ ...prev, role: currentUser?.role?.id}));
    }
  }, [currentUser]);

  const handleUserTypeChange = (e) => {
    setUserType(e.target.value);
    if (!isEntityLocked) setFormData(f => ({ ...f, entity: '' }));
    if (!isClientLocked) setFormData(f => ({ ...f, client: '' }));
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
    };

    try {
      await updateUser(id, payload);
      setShowSuccessModal(true);
    } catch (err) {
      console.error("Failed to update user:", err);
      setErrorMessage(err.response?.data?.message || "Failed to update user.");
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    navigate('/users');
  };

  if (isLoading || !currentUser) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }
  return (
    <Container fluid className="px-0">
      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-white p-3">
              <Row className="justify-content-between align-items-center">
                <Col><h1 className="h3 mb-0 text-primary">EDIT ADMIN USER</h1></Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-4 p-md-5">
              <Form onSubmit={handleSubmit}>

                {/* --- Dynamic Radio Buttons --- */}
                {visibleUserTypes.length > 0 && (
                  <Form.Group as={Row} className="mb-3">
                    <Col sm={10}>
                    {!isClientAccount && !isEntityAccount && visibleUserTypes.includes('platform') && (
                        <Form.Check
                          type="radio"
                          label="Platform"
                          name="userType"
                          value="platform"
                          checked={userType === 'platform'}
                          onChange={handleUserTypeChange}
                          inline
                          disabled={id}
                        />
                      )}
                      {!isClientAccount && visibleUserTypes.includes('entity') && (
                        <Form.Check
                          type="radio"
                          label="Entity"
                          name="userType"
                          value="entity"
                          checked={userType === 'entity'}
                          onChange={handleUserTypeChange}
                          inline
                          disabled={id}
                        />
                      )}
                      {visibleUserTypes.includes('client') && (
                        <Form.Check
                          type="radio"
                          label="Client of an entity"
                          name="userType"
                          value="client"
                          checked={userType === 'client'}
                          onChange={handleUserTypeChange}
                          inline
                          disabled={id}
                        />
                      )}
                    </Col>
                  </Form.Group>
                )}

                {(!isEntityLocked || !isClientLocked) && userType !== 'platform' && (
                <Row className="mb-3">
                  {/* Entity Dropdown */}
                  {!isClientAccount && <Form.Group as={Col} md={6} controlId="entity">
                    <Form.Label>ENTITY*</Form.Label>
                    <Form.Select required name="entity" value={formData.entity} onChange={handleChange} disabled={true}>
                      <option value="">Select...</option>
                      {entitiesList.map(e => (<option key={e.id} value={e.id}>{e.name}</option>))}
                    </Form.Select>
                  </Form.Group>}

                  {/* Client Dropdown */}
                    {userType === 'client' && !isClientAccount && (
                      <Form.Group as={Col} md={6} controlId="client">
                      <Form.Label>CLIENT*</Form.Label>
                      <Form.Select required name="client" value={formData.client} onChange={handleChange} disabled={true}>
                        <option value="">Select...</option>
                        {availableClients.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
                      </Form.Select>
                    </Form.Group>
                  )}
                </Row>
                )}

                <Row className="mb-3">
                  <Form.Group as={Col} md={6} controlId="role">
                    <Form.Label>ROLE*</Form.Label>
                    <Form.Control
                      required
                      name="role"
                      value={
                        roleDisplayName ||
                        filteredRoles.find((r) => String(r.id) === String(formData.role))?.name ||
                        ''
                      }
                      readOnly
                      disabled
                    />
                  </Form.Group>
                </Row>

                <Row className="mb-3">
                  <Form.Group as={Col} md={6} controlId="firstName">
                    <Form.Label>FIRST NAME*</Form.Label>
                    <Form.Control required name="firstName" value={formData.firstName} onChange={handleChange} />
                  </Form.Group>
                  <Form.Group as={Col} md={6} controlId="lastName">
                    <Form.Label>LAST NAME*</Form.Label>
                    <Form.Control required name="lastName" value={formData.lastName} onChange={handleChange} />
                  </Form.Group>
                </Row>
                <Row className="mb-4">
                  <Form.Group as={Col} md={6} controlId="email">
                    <Form.Label>EMAIL*</Form.Label>
                    <Form.Control required type="email" name="email" value={formData.email} onChange={handleChange} disabled={true} />
                  </Form.Group>
                </Row>

                <div className="d-flex justify-content-center gap-2">
                  <Button as={Link} to="/users" variant="secondary">Cancel</Button>
                  <Button variant="success" type="submit" className="text-white" disabled={isSubmitting}>
                    {isSubmitting ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Updating...</> : 'Update'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* --- Modals --- */}
      <Modal show={showSuccessModal} onHide={handleSuccessClose} centered backdrop="static">
        <Modal.Header closeButton><Modal.Title className="text-success">Success</Modal.Title></Modal.Header>
        <Modal.Body>User updated successfully!</Modal.Body>
        <Modal.Footer><Button variant="success" onClick={handleSuccessClose}>OK</Button></Modal.Footer>
      </Modal>

      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
        <Modal.Header closeButton><Modal.Title className="text-danger">Error</Modal.Title></Modal.Header>
        <Modal.Body>{errorMessage}</Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowErrorModal(false)}>Close</Button></Modal.Footer>
      </Modal>
    </Container>
  );
}

export default EditUser;